(() => {
  const theme = window.theme;

  if (!customElements.get('media-gallery')) {
    customElements.define(
      'media-gallery',
      class MediaGallery extends HTMLElement {
        connectedCallback() {
          this.sliderComponent = this.querySelector('slider-component');
          this.items = Array.from(this.querySelectorAll('.product-gallery__item'));
          this.thumbs = Array.from(this.querySelectorAll('[data-thumb-index]'));
          this.zoom = this.querySelector('.product-zoom');
          this.current = 0;

          const selected = this.dataset.selectedMedia;
          if (selected) {
            const index = this.indexOf(selected);
            if (index > 0) requestAnimationFrame(() => this.goTo(index, false));
          }

          this.onSelect = (event) => {
            if (event.detail.sectionId !== this.dataset.section) return;
            const index = this.indexOf(event.detail.mediaId);
            if (index > -1) this.goTo(index);
          };
          document.addEventListener('media:select', this.onSelect);

          this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.thumbIndex))));

          if (this.sliderComponent) {
            this.sliderComponent.addEventListener('slider:update', () => this.onSlide());
          }

          this.querySelectorAll('[data-zoom-index]').forEach((button) =>
            button.addEventListener('click', () => this.openZoom(Number(button.dataset.zoomIndex), button))
          );

          if (this.zoom) {
            this.zoomItems = Array.from(this.zoom.querySelectorAll('[data-zoom-item]'));
            this.zoom.querySelector('[data-zoom-prev]').addEventListener('click', () => this.stepZoom(-1));
            this.zoom.querySelector('[data-zoom-next]').addEventListener('click', () => this.stepZoom(1));
            this.zoom.addEventListener('keydown', (event) => {
              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                event.preventDefault();
                this.stepZoom(1);
              } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                event.preventDefault();
                this.stepZoom(-1);
              }
            });
          }
        }

        disconnectedCallback() {
          document.removeEventListener('media:select', this.onSelect);
        }

        indexOf(mediaId) {
          return this.items.findIndex((item) => item.dataset.mediaId === String(mediaId));
        }

        goTo(index, smooth = true) {
          const slider = this.sliderComponent && this.sliderComponent.slider;
          const item = this.items[index];
          if (!slider || !item) return;
          slider.scrollTo({ left: item.offsetLeft - slider.offsetLeft, behavior: smooth && !theme.prefersReducedMotion() ? 'smooth' : 'auto' });
          this.setCurrent(index);
        }

        onSlide() {
          const slider = this.sliderComponent.slider;
          const width = slider.clientWidth || 1;
          const index = Math.round(slider.scrollLeft / width);
          if (index !== this.current) this.setCurrent(index);
        }

        setCurrent(index) {
          this.current = index;
          this.thumbs.forEach((thumb, i) => thumb.toggleAttribute('aria-current', i === index));
          this.items.forEach((item, i) => {
            if (i === index) return;
            item.querySelectorAll('video').forEach((video) => video.pause());
          });
        }

        openZoom(index, trigger) {
          if (!this.zoom) return;
          theme.openDialog(this.zoom, trigger);
          const item = this.zoomItems.find((el) => Number(el.dataset.zoomItem) === index) || this.zoomItems[0];
          this.zoomIndex = this.zoomItems.indexOf(item);
          requestAnimationFrame(() => {
            item.scrollIntoView({ block: 'start' });
            item.focus({ preventScroll: true });
          });
        }

        stepZoom(direction) {
          if (!this.zoomItems.length) return;
          this.zoomIndex = Math.max(0, Math.min(this.zoomItems.length - 1, (this.zoomIndex || 0) + direction));
          const item = this.zoomItems[this.zoomIndex];
          item.scrollIntoView({ block: 'start', behavior: theme.prefersReducedMotion() ? 'auto' : 'smooth' });
          item.focus({ preventScroll: true });
        }
      }
    );
  }

  if (!customElements.get('size-guide')) {
    customElements.define(
      'size-guide',
      class SizeGuide extends HTMLElement {
        connectedCallback() {
          this.buttons = Array.from(this.querySelectorAll('[data-unit]'));
          this.cells = Array.from(this.querySelectorAll('td[data-cm]'));
          this.buttons.forEach((button) => button.addEventListener('click', () => this.setUnit(button.dataset.unit)));

          this.tabs = Array.from(this.querySelectorAll('[data-guide-tab]'));
          this.panels = Array.from(this.querySelectorAll('[data-guide-panel]'));
          this.tabs.forEach((tab) => tab.addEventListener('click', () => this.showPanel(tab.dataset.guideTab)));

          this.finder = this.querySelector('[data-finder]');
          if (this.finder) {
            this.height = this.finder.querySelector('[data-finder-height]');
            this.weight = this.finder.querySelector('[data-finder-weight]');
            this.selectButton = this.finder.querySelector('[data-finder-select]');
            [this.height, this.weight].forEach((input) => input.addEventListener('input', () => this.updateFinder()));
            this.selectButton.addEventListener('click', () => this.selectSize(this.selectButton.dataset.size));
          }
          this.toggleFinder();
        }

        activePanel() {
          return this.panels.find((panel) => !panel.hidden) || this.panels[0];
        }

        showPanel(key) {
          this.tabs.forEach((tab) => tab.setAttribute('aria-selected', String(tab.dataset.guideTab === key)));
          this.panels.forEach((panel) => (panel.hidden = panel.dataset.guidePanel !== key));
          this.toggleFinder();
        }

        // Colonnes « Tu mesures (cm) » et « Tu pèses (kg) » du tableau affiché ; sans elles, pas de calculateur
        finderColumns(panel) {
          const headers = panel ? Array.from(panel.querySelectorAll('thead th')).map((th) => th.textContent.toLowerCase()) : [];
          return {
            height: headers.findIndex((h) => h.includes('cm') && /mesure|hauteur|height/.test(h)),
            weight: headers.findIndex((h) => h.includes('kg'))
          };
        }

        toggleFinder() {
          if (!this.finder) return;
          const columns = this.finderColumns(this.activePanel());
          this.finder.hidden = columns.height < 1 || columns.weight < 1;
          if (!this.finder.hidden) this.updateFinder();
        }

        updateFinder() {
          const result = this.finder.querySelector('[data-finder-result]');
          const height = Number(this.height.value);
          const weight = Number(this.weight.value);
          const panel = this.activePanel();
          const rows = Array.from(panel.querySelectorAll('tbody tr'));
          rows.forEach((row) => row.classList.remove('is-match'));
          if (!(height >= 140 && height <= 215 && weight >= 35 && weight <= 160) || !rows.length) {
            result.hidden = true;
            return;
          }
          const columns = this.finderColumns(panel);
          // « 165-172 » → borne haute 172 ; la première ligne qui contient la mesure, sinon la dernière
          const upper = (row, index) => {
            const numbers = (row.children[index] ? row.children[index].textContent : '').match(/\d+(?:[.,]\d+)?/g) || [];
            return numbers.length ? parseFloat(numbers[numbers.length - 1].replace(',', '.')) : Infinity;
          };
          const indexFor = (value, column) => {
            const found = rows.findIndex((row) => value <= upper(row, column));
            return found === -1 ? rows.length - 1 : found;
          };
          const byHeight = indexFor(height, columns.height);
          const byWeight = indexFor(weight, columns.weight);
          const index = Math.max(byHeight, byWeight);
          const size = rows[index].children[0].textContent.trim();
          rows[index].classList.add('is-match');

          this.finder.querySelector('[data-finder-size]').innerHTML = this.dataset.result.replace('__SIZE__', `<strong>${size}</strong>`);
          const hint = this.finder.querySelector('[data-finder-hint]');
          const smaller = Math.min(byHeight, byWeight);
          hint.hidden = smaller === index;
          if (!hint.hidden) hint.textContent = this.dataset.smaller.replace('__SIZE__', rows[smaller].children[0].textContent.trim());

          const input = this.sizeInput(size);
          this.selectButton.hidden = !input;
          if (input) {
            const unavailable = input.classList.contains('is-unavailable');
            this.selectButton.dataset.size = size;
            this.selectButton.textContent = (unavailable ? this.dataset.unavailable : this.dataset.select).replace('__SIZE__', size);
            this.selectButton.toggleAttribute('disabled', unavailable);
          }
          result.hidden = false;
        }

        // Le bouton radio de cette taille dans le sélecteur de la fiche produit
        sizeInput(size) {
          const root = this.closest('[data-product-root]');
          if (!root) return null;
          const wanted = size.toLowerCase().replace(/\s/g, '');
          return Array.from(root.querySelectorAll('variant-picker input[type="radio"]')).find((input) => input.value.toLowerCase().replace(/\s/g, '') === wanted) || null;
        }

        selectSize(size) {
          const input = this.sizeInput(size);
          if (!input || input.classList.contains('is-unavailable')) return;
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          theme.track('vtx_size_finder_select', { size, ...theme.productContext(input) });
          const dialog = this.closest('dialog');
          if (dialog && dialog.open) dialog.close();
        }

        setUnit(unit) {
          this.buttons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.unit === unit)));
          this.cells.forEach((cell) => {
            const raw = cell.dataset.cm;
            if (unit === 'cm') {
              cell.textContent = raw;
              return;
            }
            cell.textContent = raw.replace(/\d+(?:[.,]\d+)?/g, (match) => {
              const value = parseFloat(match.replace(',', '.')) / 2.54;
              return (Math.round(value * 10) / 10).toString();
            });
          });
        }
      }
    );
  }

  if (!customElements.get('product-stories')) {
    customElements.define(
      'product-stories',
      class ProductStories extends HTMLElement {
        connectedCallback() {
          this.dialog = this.querySelector('dialog');
          if (!this.dialog) return;
          this.slides = Array.from(this.dialog.querySelectorAll('[data-story-slide]'));
          this.bars = Array.from(this.dialog.querySelectorAll('.stories-viewer__bar-fill'));
          this.title = this.dialog.querySelector('[data-story-title]');
          this.avatar = this.dialog.querySelector('[data-story-avatar]');
          this.toggleButton = this.dialog.querySelector('[data-story-toggle]');
          this.muteButton = this.dialog.querySelector('[data-story-mute]');
          this.muted = true;
          this.paused = false;

          this.querySelectorAll('[data-story-index]').forEach((button) =>
            button.addEventListener('click', () => this.open(Number(button.dataset.storyIndex), button))
          );
          this.dialog.querySelector('[data-story-prev]').addEventListener('click', () => this.show(this.index - 1));
          this.dialog.querySelector('[data-story-next]').addEventListener('click', () => this.next());
          this.toggleButton.addEventListener('click', () => this.togglePause());
          this.muteButton.addEventListener('click', () => this.toggleMute());
          this.dialog.addEventListener('close', () => this.stopAll());
          this.dialog.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowRight') this.next();
            if (event.key === 'ArrowLeft') this.show(this.index - 1);
          });
          this.slides.forEach((slide) => {
            const video = slide.querySelector('video');
            if (!video) return;
            video.removeAttribute('controls');
            video.muted = true;
            video.addEventListener('timeupdate', () => this.progress(video));
            video.addEventListener('ended', () => this.next());
          });
        }

        open(index, trigger) {
          theme.openDialog(this.dialog, trigger);
          this.show(index);
        }

        video(index = this.index) {
          const slide = this.slides[index];
          return slide ? slide.querySelector('video') : null;
        }

        show(index) {
          if (index < 0) index = 0;
          if (index >= this.slides.length) return this.dialog.close();
          this.stopAll();
          this.index = index;
          this.slides.forEach((slide, i) => (slide.hidden = i !== index));
          this.bars.forEach((bar, i) => (bar.style.transform = `scaleX(${i < index ? 1 : 0})`));
          const slide = this.slides[index];
          this.title.textContent = slide.dataset.title || '';
          this.avatar.style.backgroundImage = slide.dataset.avatar ? `url("${slide.dataset.avatar}")` : '';
          const video = this.video();
          if (video) {
            video.muted = this.muted;
            video.currentTime = 0;
            this.paused = false;
            this.syncButtons();
            const playing = video.play();
            if (playing && playing.catch) playing.catch(() => this.setPaused(true));
          }
        }

        next() {
          this.show(this.index + 1);
        }

        progress(video) {
          if (this.slides[this.index] !== video.closest('[data-story-slide]')) return;
          const bar = this.bars[this.index];
          if (bar && video.duration) bar.style.transform = `scaleX(${video.currentTime / video.duration})`;
        }

        togglePause() {
          this.setPaused(!this.paused);
        }

        setPaused(paused) {
          this.paused = paused;
          const video = this.video();
          if (video) paused ? video.pause() : video.play().catch(() => {});
          this.syncButtons();
        }

        toggleMute() {
          this.muted = !this.muted;
          const video = this.video();
          if (video) video.muted = this.muted;
          this.syncButtons();
        }

        syncButtons() {
          this.toggleButton.setAttribute('aria-label', this.paused ? this.toggleButton.dataset.labelPlay : this.toggleButton.dataset.labelPause);
          this.toggleButton.querySelector('[data-icon-pause]').hidden = this.paused;
          this.toggleButton.querySelector('[data-icon-play]').hidden = !this.paused;
          this.muteButton.setAttribute('aria-label', this.muted ? this.muteButton.dataset.labelUnmute : this.muteButton.dataset.labelMute);
          this.muteButton.querySelector('[data-icon-muted]').hidden = !this.muted;
          this.muteButton.querySelector('[data-icon-sound]').hidden = this.muted;
        }

        stopAll() {
          this.slides.forEach((slide) => {
            const video = slide.querySelector('video');
            if (video) video.pause();
          });
        }
      }
    );
  }

  if (!customElements.get('delivery-estimate')) {
    customElements.define(
      'delivery-estimate',
      class DeliveryEstimate extends HTMLElement {
        connectedCallback() {
          const config = theme.delivery;
          if (!config) return;
          const holidays = new Set((config.holidays || []).map((d) => String(d).trim()).filter(Boolean));
          const pad = (n) => String(n).padStart(2, '0');
          const key = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
          const isBusinessDay = (d) => {
            const day = d.getDay();
            if (config.skipSaturday && day === 6) return false;
            if (config.skipSunday && day === 0) return false;
            return !holidays.has(key(d));
          };
          const addBusinessDays = (date, count) => {
            const d = new Date(date);
            let added = 0;
            while (added < count) {
              d.setDate(d.getDate() + 1);
              if (isBusinessDay(d)) added++;
            }
            return d;
          };
          const start = new Date();
          start.setHours(12, 0, 0, 0);
          const now = new Date();
          if (now.getHours() >= Number(config.cutoffHour) || !isBusinessDay(start)) {
            do {
              start.setDate(start.getDate() + 1);
            } while (!isBusinessDay(start));
          }
          const min = Number(config.minDays) || 0;
          const max = Math.max(min, Number(config.maxDays) || 0);
          const from = addBusinessDays(start, min);
          const to = addBusinessDays(start, max);
          let formatter;
          try {
            formatter = new Intl.DateTimeFormat(theme.locale || document.documentElement.lang || undefined, { weekday: 'short', day: 'numeric', month: 'short' });
          } catch (e) {
            formatter = new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
          }
          const text = this.querySelector('[data-delivery-text]');
          const strong = (d) => `<strong>${formatter.format(d)}</strong>`;
          if (min === max) {
            text.innerHTML = (this.dataset.on || theme.strings.deliveryOn || '').replace('__DATE__', strong(to));
          } else {
            text.innerHTML = (this.dataset.between || theme.strings.deliveryBetween || '')
              .replace('__START__', strong(from))
              .replace('__END__', strong(to));
          }
          this.hidden = false;
        }
      }
    );
  }

  if (!customElements.get('product-complementary')) {
    customElements.define(
      'product-complementary',
      class ProductComplementary extends HTMLElement {
        connectedCallback() {
          this.addEventListener('change', (event) => {
            const select = event.target.closest('[data-complementary-variant]');
            if (!select) return;
            const item = select.closest('.complementary-item');
            const option = select.selectedOptions[0];
            item.querySelector('.complementary-item__checkbox').value = select.value;
            const price = item.querySelector('[data-complementary-price]');
            if (price && option) price.textContent = option.dataset.price;
          });
          this.toggleVisibility();
          // Un élément masqué n'est jamais « visible » pour l'IntersectionObserver : on observe son conteneur
          const watchTarget = this.closest('.product__info') || this.parentElement || this;
          this.observer = theme.whenVisible(watchTarget, (visible) => {
            if (!visible || this.loaded) return;
            this.loaded = true;
            this.observer && this.observer.disconnect();
            this.load();
          });
        }

        toggleVisibility() {
          this.hidden = !this.querySelector('.complementary-item');
        }

        async load() {
          try {
            const response = await fetch(this.dataset.url);
            const html = await response.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const content = doc.querySelector('[data-complementary-content]');
            if (!content || !content.querySelector('.complementary-item')) return this.toggleVisibility();
            const sliderComponent = this.querySelector('slider-component');
            const clone = sliderComponent.cloneNode(true);
            clone.querySelector('[data-complementary-target]').innerHTML = content.innerHTML;
            sliderComponent.replaceWith(clone);
          } catch (e) {
            console.error(e);
          }
          this.toggleVisibility();
        }
      }
    );
  }

  if (!customElements.get('sticky-atc')) {
    customElements.define(
      'sticky-atc',
      class StickyAtc extends HTMLElement {
        connectedCallback() {
          this.root = this.closest('[data-product-root]') || document;
          // Avec le bouton « Commander » (paiement à la livraison), c'est lui que la barre remplace quand il sort de l'écran
          this.mainButton = this.root.querySelector('[data-cod-order="main"]') || this.root.querySelector('[data-main-add]');
          if (!this.mainButton) return;
          this.form = this.mainButton.form;
          this.select = this.querySelector('[data-sticky-variant]');
          this.quantity = this.querySelector('[data-sticky-quantity]');
          this.mainQuantity = this.root.querySelector('[data-main-quantity]');
          if (!this.mainQuantity && this.form) {
            this.mainQuantity = document.createElement('input');
            this.mainQuantity.type = 'hidden';
            this.mainQuantity.name = 'quantity';
            this.mainQuantity.value = '1';
            this.form.appendChild(this.mainQuantity);
          }

          this.observer = new IntersectionObserver(([entry]) => {
            // Visible dès que le bouton principal n'est pas à l'écran : au-dessus (dépassé) ou encore en dessous (premier écran sur téléphone)
            this.setVisible(!entry.isIntersecting);
          });
          this.observer.observe(this.mainButton);

          if (this.quantity && this.mainQuantity) {
            this.quantity.addEventListener('change', () => {
              this.mainQuantity.value = this.quantity.value;
              this.mainQuantity.dispatchEvent(new Event('change', { bubbles: true }));
            });
            this.mainQuantity.addEventListener('change', () => {
              if (this.quantity.value !== this.mainQuantity.value) this.quantity.value = this.mainQuantity.value;
            });
          }

          if (this.select) {
            this.select.addEventListener('change', () => this.syncPicker(this.select.value));
            this.root.addEventListener('variant:change', (event) => {
              const variant = event.detail && event.detail.variant;
              if (variant && this.select.value !== String(variant.id)) this.select.value = String(variant.id);
            });
          }
          this.setVisible(false);
        }

        disconnectedCallback() {
          this.observer && this.observer.disconnect();
        }

        setVisible(visible) {
          this.classList.toggle('is-visible', visible);
          this.setAttribute('aria-hidden', String(!visible));
          this.querySelectorAll('button, select, input').forEach((el) => (visible ? el.removeAttribute('tabindex') : el.setAttribute('tabindex', '-1')));
          this.inert = !visible;
          // La barre peut être masquée par le CSS (mode « mobile uniquement » sur ordinateur)
          document.body.classList.toggle('has-sticky-atc', visible && getComputedStyle(this).display !== 'none');
        }

        syncPicker(variantId) {
          const picker = this.root.querySelector(`variant-picker[data-section="${this.dataset.section}"]`);
          if (!picker || !picker.variants) return;
          const variant = picker.variants.find((v) => String(v.id) === String(variantId));
          if (!variant) return;
          let last = null;
          variant.options.forEach((value, i) => {
            const group = picker.querySelector(`[data-option-position="${i + 1}"]`);
            if (!group) return;
            const input = Array.from(group.querySelectorAll('input[type="radio"]')).find((r) => r.value === value);
            if (input) {
              input.checked = true;
              last = input;
            }
            const label = group.querySelector('[data-selected-value]');
            if (label) label.textContent = value;
          });
          if (last) last.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    );
  }

  if (!customElements.get('share-link')) {
    customElements.define(
      'share-link',
      class ShareLink extends HTMLElement {
        connectedCallback() {
          const status = this.querySelector('[data-share-status]');
          this.querySelector('button').addEventListener('click', async () => {
            const url = this.dataset.url || window.location.href;
            if (navigator.share) {
              try {
                await navigator.share({ title: this.dataset.title, url });
                return;
              } catch (e) {
                if (e.name === 'AbortError') return;
              }
            }
            try {
              await navigator.clipboard.writeText(url);
              if (status) status.textContent = theme.strings.copied;
            } catch (e) {
              window.prompt('', url);
            }
          });
        }
      }
    );
  }

  if (!customElements.get('product-recommendations')) {
    customElements.define(
      'product-recommendations',
      class ProductRecommendations extends HTMLElement {
        connectedCallback() {
          // « Complète ton fit » est rendu côté serveur : rien à charger
          if (!this.dataset.url) return;
          this.observer = theme.whenVisible(this, async (visible) => {
            if (!visible || this.loaded) return;
            this.loaded = true;
            this.observer && this.observer.disconnect();
            try {
              const response = await fetch(this.dataset.url);
              const html = await response.text();
              const doc = new DOMParser().parseFromString(html, 'text/html');
              const fresh = doc.querySelector('product-recommendations');
              if (fresh && fresh.querySelector('.product-card')) {
                this.innerHTML = fresh.innerHTML;
                theme.initReveal && theme.initReveal(this);
              } else {
                this.closest('.shopify-section') ? (this.closest('.shopify-section').hidden = true) : (this.hidden = true);
              }
            } catch (e) {
              this.hidden = true;
            }
          });
        }
      }
    );
  }
})();
